import axios from 'axios'
import Raven from 'raven-js'

import store from '@/store'
import poll from './poll'

const pollBookingRequest = async (fn) => {
  const bookingRequest = await fn()
  if (bookingRequest.data[0].pk && bookingRequest.data[0].fields) {
    const bookingRequestId = bookingRequest.data[0].pk
    if (bookingRequest.data[0].fields.state !== 'done') {
      const pollResult = await poll({
        fn: () => axios.get('/4.0/twip/requests/' + bookingRequestId + '/'),
        validate: result => result.data.fields && result.data.fields.state === 'done',
        interval: 500,
        maxAttempts: 60
      })
      if (pollResult.data.fields.response_status === 500) {
        throw new Error('Internal Server Error')
      }
      return pollResult
    }
  } else {
    throw new Error('bookingRequest has no fiels object')
  }
}

const getAvaiableSlots = async (stationId, vehicleId, cart) => {
  try {
    let deals = [cart.offerId]
    if (cart.addons) {
      deals = deals.concat(cart.addons)
    }
    const pollResult = await pollBookingRequest(() => axios.post('/4.0/twip/bookings/', {
      station: stationId,
      deals: JSON.stringify(deals),
    }))
    let bookingId
    if (pollResult.data.fields.updated_uri) {
      bookingId = pollResult.data.fields.updated_uri.split('/').pop()
    }
    else {
      throw new Error('no bookingId retrieved')
    }

    const booking = await axios.get('/4.0/twip/bookings/' + bookingId + '/')
    if (booking.data.fields) {
      const proposedDropoffTimes = booking.data.fields.proposed_dropoff_times
      store.commit('Cart/setBookingId', { bookingId, vehicleId })
      store.commit('Cart/setProposedDropoffTimes', proposedDropoffTimes)
      return
    } else {
      throw new Error('booking has no fiels object')
    }
  } catch (err) {
    console.log(err)
    if (!(err.reponse && err.reponse.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const setBookingDropOffTime = async (bookingId, dropoffTime) => {
  try {
    await pollBookingRequest(() => axios.put('/4.0/twip/bookings/' + bookingId + '/', {
      dropoff_time: dropoffTime
    }))

    const booking = await axios.get('/4.0/twip/bookings/' + bookingId + '/')
    if (booking.data.fields && booking.data.fields.proposed_pickup_times !== null && booking.data.fields.proposed_pickup_times.length > 0) {
      store.commit('Cart/setDropoffTime', { bookingId, dropoffTime })
      return booking.data.fields.proposed_pickup_times
    } else {
      throw new Error('no pickup times proposed')
    }
  } catch (err) {
    console.log(err)
    if (!(err.reponse && err.reponse.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const setBookingPickupTimeWithPurchase = async (bookingId, pickupTime, immat, vehicleType, offerId, addons) => {
  try {
    await axios.put('/4.0/twip/bookings/' + bookingId + '/', {
      pickup_time: pickupTime,
    })
    await pollBookingRequest(() => axios.put('/4.0/twip/bookings/' + bookingId + '/', {
      vehicle_type: vehicleType,
      vehicle_registration: immat
    }))
    // add purchase
    await pollBookingRequest(() => axios.post('/4.0/twip/bookings/' + bookingId + '/purchases/', {
      deal: offerId
    }))
    // add addons
    if (addons.length > 0) {
      await Promise.all(addons.map(addon => pollBookingRequest(() => axios.post('/4.0/twip/bookings/' + bookingId + '/purchases/', {
        deal: addon
      }))))
    }
    await axios.get('/4.0/twip/bookings/' + bookingId + '/')
    store.commit('Cart/setPickupTime', { bookingId, pickupTime })
  } catch (err) {
    console.log(err)
    if (!(err.reponse && err.reponse.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const setBookingPickupTime = async (bookingId, pickupTime) => {
  try {
    await axios.put('/4.0/twip/bookings/' + bookingId + '/', {
      pickup_time: pickupTime,
    })
    store.commit('Cart/setPickupTime', { bookingId, pickupTime })
  } catch (err) {
    console.log(err)
    if (!(err.reponse && err.reponse.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const updateOrCreatePurchase = async (bookingId, immat, vehicleType, offerId, addons) => {
  // FIX ME:
  // the booking API expect suv instead of suv_4x4
  if (vehicleType === 'suv_4x4') {
    vehicleType = 'suv'
  }
  try {
    await pollBookingRequest(() => axios.put('/4.0/twip/bookings/' + bookingId + '/', {
      vehicle_type: vehicleType,
      vehicle_registration: immat
    }))

    // delete purchase if exists
    const booking = await axios.get('/4.0/twip/bookings/' + bookingId + '/')
    if (booking.data.fields.purchases.length > 0) {
      const headers = { 'Content-Type': 'application/json' }
      for (const purchase of booking.data.fields.purchases) {
        await pollBookingRequest(() => axios.delete('/4.0/twip/bookings/' + bookingId + '/purchases/' + purchase.pk + '/', { headers }))
      }
    }

    // add purchase
    await pollBookingRequest(() => axios.post('/4.0/twip/bookings/' + bookingId + '/purchases/', {
      deal: offerId
    }))
    // add addons
    if (addons.length > 0) {
      await Promise.all(addons.map(addon => pollBookingRequest(() => axios.post('/4.0/twip/bookings/' + bookingId + '/purchases/', {
        deal: addon
      }))))
    }
    await axios.get('/4.0/twip/bookings/' + bookingId + '/')

  } catch (err) {
    console.log(err)
    if (!(err.reponse && err.reponse.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const cancelBooking = async (bookingId, canceled_reason = '', canceled_type = '') => {
  try {
    await pollBookingRequest(() => axios.post('/4.0/twip/bookings/' + bookingId + '/cancel/', { canceled_reason: canceled_reason, canceled_type: canceled_type }))
    // First changed the state to cancel
    // store.dispatch('Bookings/setBookingState', { booking_id: bookingId, booking_state: 'canceled' })
    store.dispatch('SimpleBookings/setBookingState', { booking_id: bookingId, booking_state: 'canceled' })
    // then, update all booking in the background to get bill and credit notes links
    // store.dispatch('Bookings/getBookings', null, { root: true })
    await store.dispatch('SimpleBookings/getBookings', null, { root: true })
  } catch (err) {
    Raven.captureException(err)
    store.dispatch('SnackBar/setSnack', { snack: 'Une erreur s\'est produite lors de l\'annulation de votre booking', type: 'error' })
  }
}

const modifyBooking = async (bookingId, state = '', updated_booking_state = '', dropoff_time = '', pickup_time = '') => {
  try {
    await pollBookingRequest(() => axios.put('/4.0/twip/bookings/' + bookingId + '/',
      {
        'updated_booking_state': updated_booking_state,
        'dropoff_time': dropoff_time,
        'pickup_time': pickup_time
      }
    ))
    // Wait for rpc response and store new state
    // store.dispatch('Bookings/setBookingState', { booking_id: bookingId, booking_state: state })
    store.dispatch('SimpleBookings/setBookingState', { booking_id: bookingId, booking_state: state })
    // Then get the booking
    const booking = await axios.get('/4.0/twip/bookings/' + bookingId + '/')

    if (booking.data.fields.proposed_dropoff_times) {
      // get the proposed dropoff time
      store.commit('Cart/setProposedDropoffTimes', booking.data.fields.proposed_dropoff_times)
    }
    if (booking.data.fields.proposed_pickup_times) {
      // get the proposed dropoff time
      store.commit('Cart/setProposedPickupTimes', booking.data.fields.proposed_pickup_times)
    }
    // then, update all booking in the background to get bill and credit notes links
  } catch (err) {
    Raven.captureException(err)
    store.dispatch('SnackBar/setSnack', { snack: 'Une erreur s\'est produite lors de la modification de votre booking', type: 'error' })
  }
}

const readBookings = async () => {
  try {
    const result = await axios.get('/4.0/twip/bookings/')
    store.commit('Bookings/setBookings', result.data)
  } catch (err) {
    Raven.captureException(err)
    store.dispatch('SnackBar/setSnack', { snack: 'Une erreur s\'est produite lors de la récupération des réservations', type: 'error' })
  }
}

const readSimpleBookings = async () => {
  try {
    const result = await axios.get('/4.0/twip/simple-bookings/')
    store.commit('SimpleBookings/setBookings', result.data)
  } catch (err) {
    Raven.captureException(err)
    store.dispatch('SnackBar/setSnack', { snack: 'Une erreur s\'est produite lors de la récupération des réservations', type: 'error' })
  }
}

export {
  getAvaiableSlots,
  setBookingDropOffTime,
  setBookingPickupTime,
  setBookingPickupTimeWithPurchase,
  updateOrCreatePurchase,
  modifyBooking,
  cancelBooking,
  readBookings,
  readSimpleBookings
}
